import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { CommonFieldConfig, GenericField, GenericFormType } from '@neo/blocks/forms/types'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@neo/primitives/collapsible'
import { Tooltip } from '@neo/primitives/tooltip'
import { Typography } from '@neo/primitives/typography'
import isEqual from 'lodash/isEqual'
import { ChevronDown, Info } from 'lucide-react'
import { type ReactNode, useMemo, useState } from 'react'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import { type ZodTypeAny, z } from 'zod'
import { NodevarList } from '../../components/nodevar-list'
import { mcpAppValidation } from '../../config/mcp-tool-validation'
import { DEFAULT_PYTHON_CODE } from '../../config/nodes.config'
import { v2validations } from '../../config/validation.config'
import type { McpToolNodeData } from '../../types'
import { getMcpInputSchema, mcpSourceConfig, normalizeCodeName } from './mcp-tool-config'
import { missingInputs } from './mcp-tool-missing-inputs'
import {
  getToolInputs,
  initialInputsFor,
  linkedInputs,
  type McpToolInput,
  type McpToolInputKind,
} from './mcp-tool-schema'

export type McpToolFormValues = McpToolNodeData['config']
type McpToolForm = GenericFormType<McpToolFormValues>
type McpToolField = GenericField<McpToolFormValues>

// form-core reads `.`, `[`, `]` and an all-digit segment in a field name as a path, so the form
// holds any other property name under a reversible alias; the saved config keeps the real name.
const PLAIN_KEY = /^(?!\d+$)[\w-]+$/
const ALIAS_PREFIX = '~'

const toFieldKey = (key: string) =>
  PLAIN_KEY.test(key) ? key : `${ALIAS_PREFIX}${encodeURIComponent(key).replace(/\./g, '%2E')}`

const fromFieldKey = (fieldKey: string) =>
  fieldKey.startsWith(ALIAS_PREFIX) ? decodeURIComponent(fieldKey.slice(1)) : fieldKey

const renameKeys = (inputs: Record<string, string>, rename: (key: string) => string) =>
  Object.fromEntries(Object.entries(inputs).map(([key, value]) => [rename(key), value]))

export const mcpToolFormValues = {
  load: (config: McpToolFormValues): McpToolFormValues => ({
    ...mcpSourceConfig(config),
    ...(config.source === 'custom_code' && { code_body: config.code_body || DEFAULT_PYTHON_CODE }),
    inputs: renameKeys(config.inputs ?? {}, toFieldKey),
  }),
  save: (values: McpToolFormValues): McpToolFormValues => {
    const config = mcpSourceConfig(values)
    const inputs = renameKeys(values.inputs ?? {}, fromFieldKey)
    if (config.source !== 'custom_code') return { ...config, inputs }
    const keys = new Set(getToolInputs(getMcpInputSchema(config)).map((input) => input.key))
    return {
      ...config,
      code_name: normalizeCodeName(config.code_name ?? ''),
      inputs: Object.fromEntries(Object.entries(inputs).filter(([key]) => keys.has(key))),
    }
  },
}

export const resetCustomCodeInputs = (
  form: McpToolForm,
  schema: McpToolFormValues['code_function_schema'],
) => {
  const inputSchema = getMcpInputSchema({ source: 'custom_code', code_function_schema: schema })
  // Incomplete JSON while typing says nothing about whether a parameter was removed.
  if (!inputSchema || isEqual(inputSchema, getMcpInputSchema(form.state.values))) return
  const saved = renameKeys(form.state.values.inputs ?? {}, fromFieldKey)
  const inputs = getToolInputs(inputSchema)
  resetToolInputs(
    form,
    Object.fromEntries(inputs.map((input) => [input.key, saved[input.key] ?? ''])),
  )
}

// form-core keeps an unmounted field registered and validating it, so the previous tool's
// required inputs would block Save unless their fields are deleted before the values go.
export const resetToolInputs = (form: McpToolForm, inputs: Record<string, string>) => {
  for (const name of Object.keys(form.fieldInfo)) {
    if (name.startsWith('inputs.')) form.deleteField(name as `inputs.${string}`)
  }
  form.setFieldValue('inputs', renameKeys(inputs, toFieldKey))
}

interface FieldLabelProps {
  children: ReactNode
  required?: boolean
  description?: string
}

export const FieldLabel = ({ children, required, description }: FieldLabelProps) => (
  <span className="flex items-center gap-1">
    {children}
    {required && <span className="text-status-danger">*</span>}
    {description && (
      <Tooltip content={description}>
        <Info className="size-3.5 text-muted-foreground" />
      </Tooltip>
    )}
  </span>
)

const SEARCHABLE_OPTIONS = 6
const TEXTAREA_KINDS: McpToolInputKind[] = ['json', 'files']

// The schema describes the parts agent-runner builds from the link, never the link itself.
const KIND_HELP: Partial<Record<McpToolInputKind, string>> = {
  file: 'A link to the file. It is downloaded and sent to the app (max 10 MB)',
  files: 'Links to the files. They are downloaded and sent to the app (max 10 MB in total)',
}

const fieldName = (input: McpToolInput) => `inputs.${toFieldKey(input.key)}` as const

// Whether an input is needed depends on the others, so each field asks the same check the node
// validation runs.
const schemaFor = (input: McpToolInput, valueSchema: ZodTypeAny, values: McpToolFormValues) => {
  const inputs = renameKeys(values.inputs ?? {}, fromFieldKey)
  const issue = missingInputs(getMcpInputSchema(values), inputs).find(
    ({ key }) => key === input.key,
  )
  return issue ? z.unknown().refine(() => false, issue.message) : valueSchema.optional()
}

const validationFor =
  (
    input: McpToolInput,
    valueSchema: ZodTypeAny,
  ): CommonFieldConfig<McpToolFormValues>['zodValidation'] =>
  (values) =>
    schemaFor(input, valueSchema, values)

const failsCheck = (input: McpToolInput, valueSchema: ZodTypeAny, values: McpToolFormValues) =>
  !schemaFor(input, valueSchema, values).safeParse(values.inputs?.[toFieldKey(input.key)]).success

const inputField = (
  input: McpToolInput,
  inputs: McpToolInput[],
  nodeVars: string[],
  valueSchema: ZodTypeAny,
): McpToolField => {
  const name = fieldName(input)
  const element = TEXTAREA_KINDS.includes(input.kind) ? 'textarea' : 'input'
  const notes = [...input.hints, KIND_HELP[input.kind]]
  const helperText = [...notes, input.summary].filter(Boolean).join('. ') || undefined
  // Textarea has no helper line, so it carries the notes in the tooltip.
  const description =
    element === 'textarea'
      ? [...notes, input.description].filter(Boolean).join('. ') || undefined
      : input.description
  const common = {
    name,
    label: (
      <FieldLabel required={input.required} description={description}>
        {input.label}
      </FieldLabel>
    ),
    zodValidation: validationFor(input, valueSchema),
    onChangeListenTo: linkedInputs(input, inputs).map(fieldName),
  }
  if (input.kind === 'select') {
    return {
      ...common,
      componentType: 'select',
      inputProps: {
        options: input.options.map((value) => ({ value, label: value })),
        placeholder: input.placeholder,
        // The Select cannot pick an empty option; clearing is how an optional choice goes unset.
        clearable: !input.required,
        searchable: input.options.length > SEARCHABLE_OPTIONS,
        helperText,
      },
    }
  }
  return {
    ...common,
    componentType: element,
    inputProps: {
      placeholder: input.placeholder,
      'data-testid': `${element}-mcp-tool-${input.key}`,
      ...(element === 'input' && { helperText }),
      endIcon: (
        <NodevarList
          nodevars={nodeVars}
          className="pr-2"
          inputElementSelector={`${element === 'textarea' ? 'textarea' : ''}[name="${name}"]`}
        />
      ),
    },
  }
}

const toolField = (
  app: RExternalOrgApp | undefined,
  orgAppUuid: string | null | undefined,
  toolName: string | null | undefined,
  validation: ZodTypeAny,
): McpToolField => {
  const actions = app?.actions ?? []
  const selected = actions.find((action) => action.action_name === toolName)
  // The Select also reports a re-pick of the chosen tool; this holds the value before the change.
  let previous: string | null | undefined
  return {
    name: 'tool_name',
    componentType: 'select',
    label: <FieldLabel required>Tool</FieldLabel>,
    zodValidation: validation,
    disabled: () => !orgAppUuid,
    inputProps: {
      options: actions.map((action) => ({ value: action.action_name, label: action.action_name })),
      placeholder: orgAppUuid ? 'Select a tool' : 'Select an app first',
      emptyPlaceholder: actions.length ? undefined : 'This app has no tools',
      helperText: selected?.description,
      error: app && toolName && !selected ? 'This tool is no longer offered by the app' : undefined,
    },
    eventDataExtractor: (value: string, form) => {
      previous = form?.state.values.tool_name
      return value
    },
    afterOnChange: (values, _index, form) => {
      if (!form) return
      const schema = actions.find((action) => action.action_name === values.tool_name)
        ?.input_schema as McpToolFormValues['tool_schema'] | undefined
      // A changed schema still resets: picking the tool again is how a stale one is refreshed.
      if (values.tool_name === previous && isEqual(schema, values.tool_schema)) return
      form.setFieldValue('tool_schema', schema ?? null)
      resetToolInputs(form, initialInputsFor(schema))
    },
  }
}

interface OptionalInputsProps {
  form: McpToolForm
  fields: McpToolField[]
  startOpen: boolean
  failing: (values: McpToolFormValues) => boolean
}

const OptionalInputs = ({ form, fields, startOpen, failing }: OptionalInputsProps) => {
  const [open, setOpen] = useState(startOpen)
  // A collapsed field is unmounted and Save skips it, so one that fails its check stays shown.
  const mustShow = form.useStore((state) => failing(state.values))
  const shown = open || mustShow
  return (
    <Collapsible open={shown} onOpenChange={setOpen}>
      <CollapsibleTrigger
        className="flex items-center gap-1 text-brand-text [&[data-state=open]>svg]:rotate-180"
        data-testid="button-mcp-tool-optional-inputs"
      >
        <Typography variant="P/500" as="span">
          {shown ? 'Hide optional inputs' : `Show ${fields.length} more`}
        </Typography>
        <ChevronDown className="size-4 transition-transform" />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-4 flex flex-col gap-4">
        <FieldsRenderer form={form} fields={fields} />
      </CollapsibleContent>
    </Collapsible>
  )
}

interface McpToolInputsProps {
  form: McpToolForm
  apps: RExternalOrgApp[] | undefined
  nodeVars: string[]
}

const splitShownUpfront = (inputs: McpToolInput[]) => {
  const upfront: McpToolInput[] = []
  const optional: McpToolInput[] = []
  for (const input of inputs) {
    if (input.required || input.oneOfRequired) upfront.push(input)
    else optional.push(input)
  }
  return { upfront, optional }
}

export const McpToolInputs = ({ form, apps, nodeVars }: McpToolInputsProps) => {
  const orgAppUuid = form.useStore((state) => state.values.org_app_uuid)
  const toolName = form.useStore((state) => state.values.tool_name)
  const source = form.useStore((state) => state.values.source)
  const appSchema = form.useStore((state) => state.values.tool_schema)
  const codeSchema = form.useStore((state) => state.values.code_function_schema)
  const toolSchema = useMemo(
    () => getMcpInputSchema({ source, tool_schema: appSchema, code_function_schema: codeSchema }),
    [source, appSchema, codeSchema],
  )
  const shape = v2validations({ nodeVars }).mcp_tool.innerType().shape
  const app = apps?.find((row) => row.id === orgAppUuid)
  const inputs = getToolInputs(toolSchema)
  const toField = (input: McpToolInput) =>
    inputField(input, inputs, nodeVars, shape.inputs.valueSchema)
  const { upfront, optional } = splitShownUpfront(inputs)
  const saved = form.state.values.inputs

  return (
    <div className="col-span-2 flex flex-col gap-4">
      {source !== 'custom_code' && (
        <FieldsRenderer
          form={form}
          fields={[toolField(app, orgAppUuid, toolName, mcpAppValidation.tool_name)]}
        />
      )}
      {/* Keyed per tool: a deleted field registers with the form again only when it remounts. */}
      <div
        key={source === 'custom_code' ? JSON.stringify(toolSchema) : `${orgAppUuid}:${toolName}`}
        className="flex flex-col gap-4"
      >
        <FieldsRenderer form={form} fields={upfront.map(toField)} />
        {optional.length > 0 && (
          <OptionalInputs
            form={form}
            fields={optional.map(toField)}
            startOpen={optional.some((input) => !!saved?.[toFieldKey(input.key)]?.trim())}
            failing={(values) =>
              optional.some((input) => failsCheck(input, shape.inputs.valueSchema, values))
            }
          />
        )}
      </div>
    </div>
  )
}
